/**
 * Dashboard environment configuration.
 *
 * Reads from Vite's import.meta.env at runtime.
 * Never logs PHI or sensitive values.
 */

export interface DashboardEnv {
  /** Application profile: 'clinician' | 'ops' | 'admin' */
  appProfile: string;
  /** CNS WebSocket URL */
  wsUrl: string;
  /** CNS REST API base URL */
  apiUrl: string;
  /** Supabase project URL */
  supabaseUrl: string;
  /** Supabase anon key */
  supabaseAnonKey: string;
  /** Current environment */
  mode: string;
}

let cachedEnv: DashboardEnv | null = null;

function parseRuntimeOverrides(): {
  wsUrl?: string;
  apiUrl?: string;
} {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const apiUrl = params.get('apiUrl');
  const wsUrl = params.get('wsUrl');

  return {
    apiUrl: apiUrl?.trim() || undefined,
    wsUrl: wsUrl?.trim() || undefined,
  };
}

export function getDashboardEnv(): DashboardEnv {
  if (cachedEnv) return cachedEnv;

  const runtime = parseRuntimeOverrides();

  cachedEnv = {
    appProfile: import.meta.env.VITE_APP_PROFILE ?? 'clinician',
    wsUrl: runtime.wsUrl ?? import.meta.env.VITE_WS_URL ?? 'ws://localhost:9200',
    apiUrl: runtime.apiUrl ?? import.meta.env.VITE_API_URL ?? 'http://localhost:9200',
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? '',
    supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '',
    mode: import.meta.env.MODE ?? 'development',
  };

  return cachedEnv;
}
